import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Image as ImageIcon, Save, Upload, UserRound } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useForm } from "react-hook-form";
import { z } from "zod";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import { getEditableModule, updateEditableModule } from "../services/editableModuleService";
import { uploadFileToS3 } from "../services/uploadService";

const moduleSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  image: z.string().trim().optional(),
  position: z.string().trim().min(1, "Position is required"),
  description: z.string().trim().optional()
});

const defaultValues = {
  name: "",
  image: "",
  position: "",
  description: ""
};

export default function EditableProfileModule({
  moduleKey,
  responseKey,
  eyebrow,
  title,
  description,
  uploadFolder
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const fileInputRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty }
  } = useForm({
    defaultValues,
    resolver: zodResolver(moduleSchema)
  });

  const image = watch("image");
  const editorContent = watch("description");
  const previewName = watch("name");
  const previewPosition = watch("position");

  async function loadModule() {
    setIsLoading(true);
    const response = await getEditableModule(moduleKey);

    if (response?.success) {
      const record = response.data?.[responseKey] || {};
      reset({
        name: record.name || "",
        image: record.image || "",
        position: record.position || "",
        description: record.description || ""
      });
    } else {
      showErrorToast(response?.message || `Unable to load ${title}`);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadModule();
  }, [moduleKey]);

  function handleImageUpload(file) {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploading(true);
    uploadFileToS3(file, uploadFolder || moduleKey).then((response) => {
      setIsUploading(false);

      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        return;
      }

      setValue("image", response.data.file.url, {
        shouldDirty: true,
        shouldValidate: true
      });
      showSuccessToast("Image uploaded successfully");
    });
  }

  function handleImageDrop(event) {
    event.preventDefault();
    setIsDraggingUpload(false);
    handleImageUpload(event.dataTransfer.files?.[0]);
  }

  const onSubmit = async (values) => {
    const response = await updateEditableModule(moduleKey, values);

    if (!response?.success) {
      showErrorToast(response?.message || `Unable to save ${title}`);
      return;
    }

    const record = response.data?.[responseKey] || values;
    reset({
      name: record.name || "",
      image: record.image || "",
      position: record.position || "",
      description: record.description || ""
    });
    showSuccessToast(response.message || `${title} saved successfully`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Edit {title}</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Update the name, image, position, and rich text description shown on the website.
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <UserRound className="h-5 w-5" />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading {title.toLowerCase()} details...
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div className="space-y-5">
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2">
                      <FieldLabel>Name *</FieldLabel>
                      <Input placeholder="Enter name" {...register("name")} />
                      {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                    </div>

                    <div className="space-y-2">
                      <FieldLabel>Position *</FieldLabel>
                      <Input placeholder="Enter position" {...register("position")} />
                      {errors.position ? <FieldError>{errors.position.message}</FieldError> : null}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Image</FieldLabel>
                    <div className="flex flex-col gap-3">
                      <Input placeholder="Image URL" {...register("image")} />
                      <div
                        className={`flex min-h-32 flex-col items-center justify-center rounded-3xl border border-dashed px-4 py-6 text-center transition-colors ${
                          isDraggingUpload
                            ? "border-primary bg-primary/5"
                            : "border-slate-200 bg-slate-50/80 hover:border-primary/60 hover:bg-white"
                        }`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setIsDraggingUpload(true);
                        }}
                        onDragLeave={() => setIsDraggingUpload(false)}
                        onDrop={handleImageDrop}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) => handleImageUpload(event.target.files?.[0])}
                        />
                        <Upload className="h-6 w-6 text-primary" />
                        <p className="mt-3 text-sm font-semibold text-panel-ink">
                          {isUploading ? "Uploading..." : "Drag image here"}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="mt-3"
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Choose Image
                        </Button>
                      </div>
                    </div>
                    {errors.image ? <FieldError>{errors.image.message}</FieldError> : null}
                  </div>

                  <div className="space-y-2 editable-module-editor">
                    <style>{`
                      .editable-module-editor .ql-container {
                        height: 220px;
                        overflow-y: auto;
                      }
                      .editable-module-editor .ql-editor {
                        min-height: 220px;
                      }
                    `}</style>
                    <FieldLabel>Description</FieldLabel>
                    <ReactQuill
                      theme="snow"
                      value={editorContent || ""}
                      onChange={(content) =>
                        setValue("description", content, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                    />
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-100 bg-slate-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">
                    Preview
                  </p>
                  <div className="mt-4 overflow-hidden rounded-3xl bg-white shadow-sm">
                    {image ? (
                      <img src={image} alt={previewName || title} className="h-56 w-full object-cover" />
                    ) : (
                      <div className="flex h-56 items-center justify-center bg-slate-100 text-panel-muted">
                        <ImageIcon className="h-10 w-10" />
                      </div>
                    )}
                    <div className="p-4">
                      <h2 className="text-lg font-bold text-panel-ink">{previewName || "Name"}</h2>
                      <p className="mt-1 text-sm font-semibold text-primary">{previewPosition || "Position"}</p>
                      {editorContent ? (
                        <div
                          className="mt-3 line-clamp-4 text-sm leading-6 text-panel-muted"
                          dangerouslySetInnerHTML={{ __html: editorContent }}
                        />
                      ) : (
                        <p className="mt-3 text-sm text-panel-muted">Description preview will appear here.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end border-t border-slate-100 pt-5">
                <Button type="submit" disabled={isSubmitting || isUploading || !isDirty} className="w-full md:w-auto">
                  <Save className="mr-2 h-4 w-4" />
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
